import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { format, parseISO, addDays } from 'date-fns';
import { 
  MagnifyingGlassIcon, 
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface AccessGrant {
  id: string;
  account_id: string;
  account_name: string;
  owner_email: string;
  access_code_used: string;
  granted_at: string;
  expires_at: string;
  status: 'active' | 'expired' | 'revoked' | 'converted';
  extended_count: number;
  days_remaining: number;
}

export default function AccessGrantsManager() {
  const [grants, setGrants] = useState<AccessGrant[]>([]);
  const [filteredGrants, setFilteredGrants] = useState<AccessGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingGrant, setEditingGrant] = useState<string | null>(null);
  const [newExpirationDate, setNewExpirationDate] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired' | 'expiring_soon'>('active');

  useEffect(() => {
    fetchGrants();
  }, []);

  useEffect(() => {
    filterGrants();
  }, [grants, searchTerm, statusFilter]);

  const fetchGrants = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-get-access-grants');
      
      if (error) throw error;
      
      setGrants(data.grants || []);
    } catch (error) {
      console.error('Error fetching access grants:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterGrants = () => {
    let filtered = [...grants];

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(g => g.status === 'active');
    } else if (statusFilter === 'expired') {
      filtered = filtered.filter(g => g.status === 'expired');
    } else if (statusFilter === 'expiring_soon') {
      filtered = filtered.filter(g => g.status === 'active' && g.days_remaining <= 7);
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(grant => 
        grant.account_name?.toLowerCase().includes(search) ||
        grant.owner_email?.toLowerCase().includes(search) ||
        grant.access_code_used?.toLowerCase().includes(search)
      );
    }

    setFilteredGrants(filtered);
  };

  const handleUpdateExpiration = async (grantId: string) => {
    if (!newExpirationDate) return;

    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke('admin-update-access-grant', {
        body: {
          grantId,
          newExpirationDate
        }
      });

      if (error) throw error;

      // Refresh the list
      await fetchGrants();
      setEditingGrant(null);
      setNewExpirationDate('');
    } catch (error) {
      console.error('Error updating expiration:', error);
      alert('Failed to update expiration date');
    } finally {
      setSaving(false);
    }
  };

  const extendByDays = async (grantId: string, days: number) => {
    setSaving(true);
    try {
      const grant = grants.find(g => g.id === grantId);
      if (!grant) return;

      const newDate = addDays(parseISO(grant.expires_at), days);
      
      const { error } = await supabase.functions.invoke('admin-update-access-grant', {
        body: {
          grantId,
          newExpirationDate: newDate.toISOString(),
          extensionDays: days
        }
      });

      if (error) throw error;

      await fetchGrants();
    } catch (error) {
      console.error('Error extending grant:', error);
      alert('Failed to extend access grant');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status: string, daysRemaining: number) => {
    if (status === 'expired') {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    } else if (status === 'active' && daysRemaining <= 7) {
      return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    } else if (status === 'active') {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    return null;
  };

  const getRowColor = (status: string, daysRemaining: number) => {
    if (status === 'expired') return 'bg-red-50';
    if (status === 'active' && daysRemaining <= 3) return 'bg-red-50';
    if (status === 'active' && daysRemaining <= 7) return 'bg-yellow-50';
    return '';
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Access Grants Manager</h3>
        
        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by account name, email, or access code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="all">All Grants</option>
                <option value="active">Active Only</option>
                <option value="expired">Expired Only</option>
                <option value="expiring_soon">Expiring Soon (≤7 days)</option>
              </select>
              <button
                onClick={fetchGrants}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowPathIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4 grid grid-cols-4 gap-4">
          <div className="bg-gray-50 p-3 rounded">
            <div className="text-sm text-gray-500">Total Grants</div>
            <div className="text-xl font-semibold">{grants.length}</div>
          </div>
          <div className="bg-green-50 p-3 rounded">
            <div className="text-sm text-gray-500">Active</div>
            <div className="text-xl font-semibold text-green-600">
              {grants.filter(g => g.status === 'active').length}
            </div>
          </div>
          <div className="bg-yellow-50 p-3 rounded">
            <div className="text-sm text-gray-500">Expiring Soon</div>
            <div className="text-xl font-semibold text-yellow-600">
              {grants.filter(g => g.status === 'active' && g.days_remaining <= 7).length}
            </div>
          </div>
          <div className="bg-red-50 p-3 rounded">
            <div className="text-sm text-gray-500">Expired</div>
            <div className="text-xl font-semibold text-red-600">
              {grants.filter(g => g.status === 'expired').length}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Access Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Granted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days Left
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Extensions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    Loading grants...
                  </td>
                </tr>
              ) : filteredGrants.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No grants found
                  </td>
                </tr>
              ) : (
                filteredGrants.map((grant) => (
                  <tr key={grant.id} className={getRowColor(grant.status, grant.days_remaining)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusIcon(grant.status, grant.days_remaining)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{grant.account_name}</div>
                      <div className="text-sm text-gray-500">{grant.owner_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                        {grant.access_code_used}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(parseISO(grant.granted_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingGrant === grant.id ? (
                        <input
                          type="datetime-local"
                          value={newExpirationDate}
                          onChange={(e) => setNewExpirationDate(e.target.value)}
                          className="text-sm rounded border-gray-300"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">
                          {format(parseISO(grant.expires_at), 'MMM d, yyyy')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {grant.status === 'active' ? (
                        <span className={`font-semibold ${
                          grant.days_remaining <= 3 ? 'text-red-600' :
                          grant.days_remaining <= 7 ? 'text-yellow-600' :
                          'text-gray-900'
                        }`}>
                          {grant.days_remaining}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {grant.extended_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {editingGrant === grant.id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdateExpiration(grant.id)}
                            disabled={saving || !newExpirationDate}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingGrant(null);
                              setNewExpirationDate('');
                            }}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingGrant(grant.id);
                              setNewExpirationDate(grant.expires_at.slice(0, 16));
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                          {grant.status === 'active' && (
                            <>
                              <button
                                onClick={() => extendByDays(grant.id, 7)}
                                disabled={saving}
                                className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                              >
                                +7d
                              </button>
                              <button
                                onClick={() => extendByDays(grant.id, 30)}
                                disabled={saving}
                                className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                              >
                                +30d
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}